import React, { useState } from "react";
import Page from "../components/Page";
import { IconDeviceDesktopAnalytics, IconPlus, IconListDetails, IconSettings2, IconRecycle, IconArrowsExchange, IconTool, IconCalculator } from "@tabler/icons-react";
import { 
  useAssets, 
  useAssetDashboard, 
  useAssetCategories,
  createAsset,
  transferAsset,
  recordMaintenance,
  disposeAsset,
  runDepreciation
} from "../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetsPage() {
  const { data: dashboard, mutate: mutateDashboard } = useAssetDashboard();
  const { data: assets, mutate: mutateAssets } = useAssets();
  const { data: categories } = useAssetCategories();
  
  const [activeTab, setActiveTab] = useState("dashboard"); // dashboard, list, reports
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Forms
  const [assetForm, setAssetForm] = useState({
    name: "", asset_id_str: "", category_id: "", purchase_date: "", purchase_price: 0,
    department: "", location: "", depreciation_method: "None", useful_life_years: 0, residual_value: 0
  });

  const [transferForm, setTransferForm] = useState({ to_location: "", to_department: "", assigned_to: "", reason: "" });
  const [maintenanceForm, setMaintenanceForm] = useState({ service_date: "", maintenance_type: "", technician: "", cost: 0, status: "Scheduled", notes: "" });
  const [disposeForm, setDisposeForm] = useState({ method: "Sold", sale_value: 0, reason: "", approved_by: "" });

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      await createAsset(assetForm);
      toast.success("Asset added successfully!");
      document.getElementById('modal_add_asset').close();
      mutateAssets();
      mutateDashboard();
    } catch (err) {
      toast.error("Failed to add asset");
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await transferAsset({
        asset_id: selectedAsset.id,
        from_location: selectedAsset.location,
        to_location: transferForm.to_location,
        from_department: selectedAsset.department,
        to_department: transferForm.to_department,
        assigned_to: transferForm.assigned_to,
        reason: transferForm.reason
      });
      toast.success("Asset transferred successfully!");
      document.getElementById('modal_transfer_asset').close();
      mutateAssets();
    } catch (err) {
      toast.error("Failed to transfer asset");
    }
  };

  const handleMaintenance = async (e) => {
    e.preventDefault();
    try {
      await recordMaintenance({ ...maintenanceForm, asset_id: selectedAsset.id });
      toast.success("Maintenance recorded!");
      document.getElementById('modal_maintenance_asset').close();
      mutateAssets();
      mutateDashboard();
    } catch (err) {
      toast.error("Failed to record maintenance");
    }
  };

  const handleDispose = async (e) => {
    e.preventDefault();
    try {
      await disposeAsset({ ...disposeForm, asset_id: selectedAsset.id });
      toast.success("Asset disposed!");
      document.getElementById('modal_dispose_asset').close();
      mutateAssets();
      mutateDashboard();
    } catch (err) {
      toast.error("Failed to dispose asset");
    }
  };

  const handleRunDepreciation = async () => {
    if (confirm("Are you sure you want to run the depreciation calculation for all active assets? This will update their current book values.")) {
      try {
        await runDepreciation();
        toast.success("Depreciation calculated and updated!");
        mutateAssets();
        mutateDashboard();
      } catch (err) {
        toast.error("Failed to calculate depreciation");
      }
    }
  };

  return (
    <Page>
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-black text-gray-800 dark:text-white">Hotel Asset Management</h1>
          <p className="text-gray-500">Track lifecycle, maintenance, and depreciation of all fixed assets.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline text-restro-green border-restro-green" onClick={handleRunDepreciation}>
            <IconCalculator /> Run Depreciation
          </button>
          <button className="btn btn-primary bg-restro-green border-restro-green hover:bg-green-700 hover:border-green-700" onClick={() => document.getElementById('modal_add_asset').showModal()}>
            <IconPlus /> Register Asset
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs tabs-boxed mb-6 bg-transparent border border-gray-200 dark:border-gray-700 p-1 w-max">
        <a className={`tab font-bold ${activeTab === 'dashboard' ? 'tab-active bg-restro-green text-white' : 'text-gray-500'}`} onClick={() => setActiveTab('dashboard')}>Overview</a>
        <a className={`tab font-bold ${activeTab === 'list' ? 'tab-active bg-restro-green text-white' : 'text-gray-500'}`} onClick={() => setActiveTab('list')}>Asset Register</a>
        <a className={`tab font-bold ${activeTab === 'reports' ? 'tab-active bg-restro-green text-white' : 'text-gray-500'}`} onClick={() => setActiveTab('reports')}>Reports</a>
      </div>

      {activeTab === 'dashboard' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">Total Assets</h3>
            <p className="text-3xl font-black">{dashboard?.total_assets || 0}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">Total Asset Cost</h3>
            <p className="text-3xl font-black text-blue-600">{dashboard?.total_cost || 0}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">Current Book Value</h3>
            <p className="text-3xl font-black text-restro-green">{dashboard?.current_book_value || 0}</p>
            <p className="text-xs text-gray-400 mt-1">Acc. Dep: {dashboard?.accumulated_depreciation || 0}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">Active Maintenance</h3>
            <p className="text-3xl font-black text-orange-500">{dashboard?.active_maintenance || 0}</p>
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="table w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500">
                <tr>
                  <th>Asset ID & Name</th>
                  <th>Department / Loc</th>
                  <th>Depreciation Method</th>
                  <th>Book Value</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(assets) && assets.length > 0 ? assets.map(asset => (
                  <tr key={asset.id} className="hover">
                    <td>
                      <p className="font-bold">{asset.asset_id_str || `AST-${asset.id}`}</p>
                      <p className="text-xs text-gray-500">{asset.name}</p>
                    </td>
                    <td>
                      <p>{asset.department || '-'}</p>
                      <p className="text-xs text-gray-500">{asset.location || '-'}</p>
                    </td>
                    <td>
                       <p>{asset.depreciation_method}</p>
                       {asset.depreciation_method !== 'None' && <p className="text-xs text-gray-500">Life: {asset.useful_life_years} yrs</p>}
                    </td>
                    <td>
                      <p className="font-bold text-restro-green">{asset.current_book_value || 0}</p>
                      <p className="text-xs text-gray-500">Cost: {asset.purchase_price}</p>
                    </td>
                    <td>
                      <span className={`badge badge-sm font-bold ${asset.status === 'Active' ? 'badge-success' : asset.status === 'Disposed' ? 'badge-error' : 'badge-warning'}`}>
                        {asset.status}
                      </span>
                    </td>
                    <td className="text-right">
                      {asset.status !== 'Disposed' && (
                        <div className="dropdown dropdown-end">
                          <label tabIndex={0} className="btn btn-xs btn-ghost text-blue-500 m-1">Options</label>
                          <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 dark:bg-gray-800 rounded-box w-52 border dark:border-gray-700">
                            <li><a onClick={() => { setSelectedAsset(asset); document.getElementById('modal_transfer_asset').showModal(); }}><IconArrowsExchange size={16}/> Transfer</a></li>
                            <li><a onClick={() => { setSelectedAsset(asset); document.getElementById('modal_maintenance_asset').showModal(); }}><IconTool size={16}/> Maintenance</a></li>
                            <li><a onClick={() => { setSelectedAsset(asset); document.getElementById('modal_dispose_asset').showModal(); }} className="text-red-500"><IconRecycle size={16}/> Dispose/Write-off</a></li>
                          </ul>
                        </div>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center p-8 text-gray-500">No assets found in register.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {["Complete Asset Register", "Asset by Category", "Asset by Department", "Asset by Location", "Asset Assignment", "Asset Transfer", "Asset Maintenance", "Depreciation Schedule", "Disposed Assets"].map((report, idx) => (
             <a href={`/dashboard/reports/generic/asset-${report.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} key={idx} className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-6 rounded-2xl hover:border-restro-green transition cursor-pointer flex justify-between items-center shadow-sm">
              <div className="flex gap-4 items-center">
                <div className="w-12 h-12 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center text-restro-green">
                  <IconListDetails />
                </div>
                <div>
                  <h3 className="font-bold text-sm">{report}</h3>
                  <p className="text-xs text-gray-500">View & Export PDF/Excel</p>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Add Asset Modal */}
      <dialog id="modal_add_asset" className="modal">
        <div className="modal-box w-11/12 max-w-5xl rounded-3xl dark:bg-gray-900 border dark:border-gray-800">
          <h3 className="font-bold text-xl mb-6">Register New Asset</h3>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4">✕</button>
          </form>
          
          <form onSubmit={handleCreateAsset}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Asset Name *</span></label>
                <input required type="text" className="input input-bordered w-full bg-transparent" value={assetForm.name} onChange={e => setAssetForm({...assetForm, name: e.target.value})} placeholder="e.g. MacBook Pro 16" />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Asset ID</span></label>
                <input type="text" className="input input-bordered w-full bg-transparent" value={assetForm.asset_id_str} onChange={e => setAssetForm({...assetForm, asset_id_str: e.target.value})} placeholder="e.g. AST-0001" />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Category</span></label>
                <select className="select select-bordered w-full bg-transparent" value={assetForm.category_id} onChange={e => setAssetForm({...assetForm, category_id: e.target.value})}>
                  <option value="">Select Category</option>
                  {Array.isArray(categories) && categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Purchase Date</span></label>
                <input required type="date" className="input input-bordered w-full bg-transparent" value={assetForm.purchase_date} onChange={e => setAssetForm({...assetForm, purchase_date: e.target.value})} />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Purchase Price</span></label>
                <input required type="number" step="0.01" className="input input-bordered w-full bg-transparent" value={assetForm.purchase_price} onChange={e => setAssetForm({...assetForm, purchase_price: e.target.value})} />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Department</span></label>
                <input type="text" className="input input-bordered w-full bg-transparent" value={assetForm.department} onChange={e => setAssetForm({...assetForm, department: e.target.value})} placeholder="e.g. Front Office" />
              </div>
              
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Location</span></label>
                <input type="text" className="input input-bordered w-full bg-transparent" value={assetForm.location} onChange={e => setAssetForm({...assetForm, location: e.target.value})} placeholder="e.g. Building A, Floor 1" />
              </div>

              <div className="form-control">
                <label className="label"><span className="label-text font-bold text-blue-600">Depreciation Method</span></label>
                <select className="select select-bordered w-full bg-blue-50 dark:bg-gray-800" value={assetForm.depreciation_method} onChange={e => setAssetForm({...assetForm, depreciation_method: e.target.value})}>
                  <option value="None">None</option>
                  <option value="Straight Line">Straight Line</option>
                  <option value="Declining Balance">Declining Balance</option>
                </select>
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text font-bold text-blue-600">Useful Life (Years)</span></label>
                <input type="number" className="input input-bordered w-full bg-blue-50 dark:bg-gray-800" value={assetForm.useful_life_years} onChange={e => setAssetForm({...assetForm, useful_life_years: e.target.value})} placeholder="e.g. 5" />
              </div>
            </div>
            
            <div className="modal-action">
              <button type="button" className="btn btn-ghost mr-2" onClick={() => document.getElementById('modal_add_asset').close()}>Cancel</button>
              <button type="submit" className="btn btn-primary bg-restro-green border-restro-green hover:bg-green-700">Save Asset</button>
            </div>
          </form>
        </div>
      </dialog>

      {/* Transfer Modal */}
      <dialog id="modal_transfer_asset" className="modal">
        <div className="modal-box rounded-3xl dark:bg-gray-900 border dark:border-gray-800">
          <form method="dialog"><button className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4">✕</button></form>
          <h3 className="font-bold text-xl mb-4">Transfer Asset <span className="text-restro-green">{selectedAsset?.name}</span></h3>
          <form onSubmit={handleTransfer} className="space-y-4">
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">New Department</span></label>
              <input required type="text" className="input input-bordered w-full bg-transparent" value={transferForm.to_department} onChange={e => setTransferForm({...transferForm, to_department: e.target.value})} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">New Location</span></label>
              <input required type="text" className="input input-bordered w-full bg-transparent" value={transferForm.to_location} onChange={e => setTransferForm({...transferForm, to_location: e.target.value})} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">Assign To (Employee)</span></label>
              <input type="text" className="input input-bordered w-full bg-transparent" value={transferForm.assigned_to} onChange={e => setTransferForm({...transferForm, assigned_to: e.target.value})} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">Transfer Reason</span></label>
              <input type="text" className="input input-bordered w-full bg-transparent" value={transferForm.reason} onChange={e => setTransferForm({...transferForm, reason: e.target.value})} />
            </div>
            <button type="submit" className="btn btn-primary w-full mt-4">Confirm Transfer</button>
          </form>
        </div>
      </dialog>

      {/* Maintenance Modal */}
      <dialog id="modal_maintenance_asset" className="modal">
        <div className="modal-box rounded-3xl dark:bg-gray-900 border dark:border-gray-800">
          <form method="dialog"><button className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4">✕</button></form>
          <h3 className="font-bold text-xl mb-4">Log Maintenance: <span className="text-orange-500">{selectedAsset?.name}</span></h3>
          <form onSubmit={handleMaintenance} className="space-y-4">
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">Service Date</span></label>
              <input required type="date" className="input input-bordered w-full bg-transparent" value={maintenanceForm.service_date} onChange={e => setMaintenanceForm({...maintenanceForm, service_date: e.target.value})} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">Maintenance Type</span></label>
              <input required type="text" placeholder="e.g. Repair, Oil Change, Calibration" className="input input-bordered w-full bg-transparent" value={maintenanceForm.maintenance_type} onChange={e => setMaintenanceForm({...maintenanceForm, maintenance_type: e.target.value})} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">Technician / Vendor</span></label>
              <input type="text" className="input input-bordered w-full bg-transparent" value={maintenanceForm.technician} onChange={e => setMaintenanceForm({...maintenanceForm, technician: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Cost</span></label>
                <input type="number" step="0.01" className="input input-bordered w-full bg-transparent" value={maintenanceForm.cost} onChange={e => setMaintenanceForm({...maintenanceForm, cost: e.target.value})} />
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text font-bold">Status</span></label>
                <select className="select select-bordered w-full bg-transparent" value={maintenanceForm.status} onChange={e => setMaintenanceForm({...maintenanceForm, status: e.target.value})}>
                  <option value="Scheduled">Scheduled</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </div>
            <button type="submit" className="btn btn-warning w-full mt-4">Save Maintenance Log</button>
          </form>
        </div>
      </dialog>

      {/* Dispose Modal */}
      <dialog id="modal_dispose_asset" className="modal">
        <div className="modal-box rounded-3xl dark:bg-gray-900 border dark:border-gray-800">
          <form method="dialog"><button className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4">✕</button></form>
          <h3 className="font-bold text-xl mb-4 text-red-500">Dispose / Write-Off Asset</h3>
          <p className="mb-4 text-sm text-gray-500">You are disposing <span className="font-bold text-gray-800 dark:text-white">{selectedAsset?.name}</span>. Current Book Value: <span className="font-bold">{selectedAsset?.current_book_value}</span></p>
          <form onSubmit={handleDispose} className="space-y-4">
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">Disposal Method</span></label>
              <select className="select select-bordered w-full bg-transparent" value={disposeForm.method} onChange={e => setDisposeForm({...disposeForm, method: e.target.value})}>
                <option value="Sold">Sold</option>
                <option value="Scrapped">Scrapped</option>
                <option value="Donated">Donated</option>
                <option value="Written Off">Written Off</option>
              </select>
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">Sale Value / Recovery (if any)</span></label>
              <input type="number" step="0.01" className="input input-bordered w-full bg-transparent" value={disposeForm.sale_value} onChange={e => setDisposeForm({...disposeForm, sale_value: e.target.value})} />
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text font-bold">Reason</span></label>
              <input required type="text" className="input input-bordered w-full bg-transparent" value={disposeForm.reason} onChange={e => setDisposeForm({...disposeForm, reason: e.target.value})} placeholder="e.g. End of useful life" />
            </div>
            <button type="submit" className="btn btn-error w-full mt-4">Confirm Disposal</button>
          </form>
        </div>
      </dialog>

    </Page>
  );
}
